import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'src/domains/common/components/button/button';
import { InputField } from 'src/components/LicenseConfig/shared/InputField/InputField';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';
import {
    ComplianceConfigurationLicenseConfigActions,
    ComplianceConfigurationLicenseConfigCurrencyPrefix,
    ComplianceConfigurationLicenseConfigFormFields,
    ComplianceConfigurationLicenseConfigFormLabel,
    ComplianceConfigurationLicenseConfigFormRow,
    ComplianceConfigurationLicenseConfigFormValue,
    ComplianceConfigurationLicenseConfigSection,
    ComplianceConfigurationLicenseConfigSectionTitle,
} from 'src/components/ComplianceConfiguration/LicenseConfig/ComplianceConfigurationLicenseConfig.style';

interface ComplianceConfigurationLicenseConfigFormPropsType {
    title: string;
    currency: CurrencyType | null;
    fields: Array<{
        key: string;
        label: string;
        formInputState: FormInputState<string, unknown>;
        originalValue: string;
    }>;
    isSaving: boolean;
    onSubmit: () => Promise<void>;
}

export const ComplianceConfigurationLicenseConfigForm = observer(
    ({
        title,
        currency,
        fields,
        isSaving,
        onSubmit,
    }: ComplianceConfigurationLicenseConfigFormPropsType): JSX.Element => {
        const currencySymbol = currency === null ? '' : moneySymbol(currency);
        const hasErrors = fields.some((field) => field.formInputState.result.value.type === 'error');

        const handleSubmit = (event: React.FormEvent): void => {
            event.preventDefault();
            void onSubmit();
        };

        return (
            <ComplianceConfigurationLicenseConfigSection data-test='compliance-configuration-license-config-form'>
                <ComplianceConfigurationLicenseConfigSectionTitle>
                    {title}
                </ComplianceConfigurationLicenseConfigSectionTitle>
                <form onSubmit={handleSubmit}>
                    <ComplianceConfigurationLicenseConfigFormFields>
                        {fields.map((field) => (
                            <ComplianceConfigurationLicenseConfigFormRow key={field.key}>
                                <ComplianceConfigurationLicenseConfigFormLabel>
                                    {field.label}
                                </ComplianceConfigurationLicenseConfigFormLabel>
                                <ComplianceConfigurationLicenseConfigFormValue>
                                    <ComplianceConfigurationLicenseConfigCurrencyPrefix>
                                        {currencySymbol}
                                    </ComplianceConfigurationLicenseConfigCurrencyPrefix>
                                    <InputField
                                        formInputState={field.formInputState}
                                        disabled={isSaving}
                                        originalValue={field.originalValue}
                                    />
                                </ComplianceConfigurationLicenseConfigFormValue>
                            </ComplianceConfigurationLicenseConfigFormRow>
                        ))}
                    </ComplianceConfigurationLicenseConfigFormFields>
                    <ComplianceConfigurationLicenseConfigActions>
                        <Button
                            dataTest='compliance-configuration-license-config-submit'
                            disabled={isSaving || hasErrors}
                            isLoading={isSaving}
                            type='submit'
                        >
                            Save configuration
                        </Button>
                    </ComplianceConfigurationLicenseConfigActions>
                </form>
            </ComplianceConfigurationLicenseConfigSection>
        );
    }
);
